import { inject, Injectable } from "@angular/core";
import { DateTimeUnit } from "luxon";
import { EnergyToolUtilsService } from "../../utils/energy-tool-utils/energy-tool-utils.service";
import { BAR_COLORS } from "../../../constants/colors/greenColors";
import { DoughnutChartData } from "../../../models/ui/chartData";
import { BarChartData, DataPoint, EnergyLossData, LineChartData } from "../../../models/ui/energyToolCharts";
import { Building } from "../../../models/api/asset/building";
import { Customer } from "../../../models/api/asset/customer";
import { GateSystem } from "../../../models/api/asset/gateSystem";
import { TelemetryEntry, TelemetryResponse } from "../../../models/shared/telemetry";
import { RealtimeTelemetryResponse } from "../../../models/ws/realtimeTelemetry";
import { Location } from "../../../models/api/asset/location";
import { BotevoDesignTokens } from "../../../../assets/themes/botevoPreset";
import { isNotNullOrUndefined } from "../../../shared/utils/utils";
@Injectable({
  providedIn: "root",
})
export class EnergyToolExtractionService {
  private energyToolUtils = inject(EnergyToolUtilsService);

  /**
   * Extracts line chart data from energy loss telemetry entries and sets it
   * using the provided callback function.
   *
   * This function converts both energy loss and energy loss during normal
   * operation timeseries data into a chart-friendly format, applying the
   * specified view level and time. If the data contains only a single point,
   * it adjusts the point radius and color for better visibility.
   *
   * @param energyLoss The telemetry entries representing energy loss.
   * @param energyLossNormalOperation The telemetry entries representing
   * energy loss during normal operation.
   * @param time The reference time in milliseconds for the data.
   * @param viewLevel The time unit for grouping the data (e.g., day, month).
   * @param setLineChart The callback function to set the line chart data.
   */

  public extractAndSetLineChartData(
    energyLoss: TelemetryEntry[] | undefined,
    energyLossNormalOperation: TelemetryEntry[] | undefined,
    time: number,
    viewLevel: DateTimeUnit,
    setLineChart: (lineChart: LineChartData[]) => void
  ) {
    if (energyLoss && energyLoss.length > 0 && energyLossNormalOperation && energyLossNormalOperation.length > 0) {
      const energyLossForChart = this.energyToolUtils.convertTimeseriesToChartData(
        energyLoss,
        this.energyToolUtils.getGroupingLevel(viewLevel),
        viewLevel,
        true,
        time
      );

      const energyLossNormalOperationForChart = this.energyToolUtils.convertTimeseriesToChartData(
        energyLossNormalOperation,
        this.energyToolUtils.getGroupingLevel(viewLevel),
        viewLevel,
        true,
        time
      );

      // labels are being set in component via setLabels function:
      const lineChartData: LineChartData[] = [
        {
          label: "",
          data: energyLossForChart,
          color: "#ffffff",
          borderColor: BotevoDesignTokens.primitive?.red?.[600],
        },
        {
          label: "",
          data: energyLossNormalOperationForChart,
          color: "#ffffff",
          backgroundColor: BotevoDesignTokens.primitive?.greenTransparent?.[400],
          borderColor: BotevoDesignTokens.primitive?.green?.[400],
          fill: true,
        },
      ];

      for (const dataset of lineChartData) {
        if (dataset.data && this.energyToolUtils.checkIfSinglePoint(dataset.data)) {
          dataset.pointRadius = 6;
          dataset.pointBackgroundColor = dataset.borderColor;
        }
      }
      setLineChart(lineChartData);
    } else {
      setLineChart([]);
    }
  }

  /**
   * Converts energy loss data for a given time range to bar chart data.
   * @param energyLossData Energy loss data for a given time range.
   * @param withAverage If true, adds a line chart data set with the average energy loss value.
   * @param time The time in milliseconds for the data.
   * @param viewLevel The time unit for grouping the data.
   * @param setBarChart Function to set the bar chart data.
   */
  public extractAndSetBarChartData(
    energyLossData: EnergyLossData[] | null,
    withAverage = false,
    time: number,
    viewLevel: DateTimeUnit,
    setBarChart: (barChart: BarChartData[]) => void
  ) {
    const groupingLevel = this.energyToolUtils.getGroupingLevel(viewLevel);
    if (energyLossData && energyLossData.length > 0) {
      const energyLossDataSets: { label: string | undefined; data: DataPoint[] }[] = [];
      for (const energyLossDataSet of energyLossData) {
        if (energyLossDataSet) {
          const energyLossForChart = this.energyToolUtils.convertTimeseriesToChartData(
            energyLossDataSet?.energy_loss,
            groupingLevel,
            viewLevel,
            true,
            time
          );
          if (energyLossForChart) {
            energyLossDataSets.push({
              label: energyLossDataSet.source.label,
              data: energyLossForChart,
            });
          }
        }
      }

      const energyLossBarChart: BarChartData[] = [];
      for (let index = 0; index < energyLossDataSets.length; index++) {
        const dataSet = energyLossDataSets[index];
        const colorIndex = index % BAR_COLORS.length;
        energyLossBarChart.push({
          label: dataSet.label,
          data: dataSet.data,
          color: "#ffffff",
          borderWidth: 1,
          backgroundColor: BAR_COLORS[colorIndex],
          order: 2,
        });
      }

      if (withAverage) {
        const averageEnergyLoss = this.energyToolUtils.calcAverage(energyLossDataSets[0].data);
        energyLossBarChart.push({
          label: "energy_tool.bar_chart.average",
          data: averageEnergyLoss,
          color: "#ffffff",
          borderWidth: 2,
          borderColor: BotevoDesignTokens.primitive?.red?.[600],
          type: "line",
          order: 1,
          pointRadius: 0,
        });
      }
      setBarChart(energyLossBarChart);
    } else {
      setBarChart([]);
    }
  }

  /**
   * Processes energy loss data to extract and set doughnut chart data.
   *
   * This function calculates the absolute and relative values of energy loss
   * for each data point and formats them for use in a doughnut chart. The
   * absolute value is the sum of the energy loss values, and the relative
   * value is the percentage of the total energy loss.
   *
   * @param data - Array of energy loss data points or null.
   * @param doughnutChartStateSetter - Function to set the state of the doughnut chart data.
   */

  public extractAndSetDoughnutChart(
    data: EnergyLossData[] | null,
    doughnutChartStateSetter: (doughnutChart: DoughnutChartData[]) => void
  ) {
    if (data) {
      const dataPoints: DoughnutChartData[] = [];
      data.forEach((dataPoint) => {
        if (dataPoint && dataPoint.energy_loss && dataPoint.source.label) {
          if (dataPoint.energy_loss?.length === undefined) {
            dataPoint.energy_loss = [...dataPoint.energy_loss];
          }
          const dataPointSum: number =
            dataPoint.energy_loss.length > 0
              ? dataPoint.energy_loss
                  .map((item) => item.value)
                  .reduce((prev, curr) => {
                    // For Testing add this: + Math.round(Math.random() * 10);
                    prev = prev + curr;
                    return prev;
                  })
              : 0;

          dataPoints.push({
            label: dataPoint.source.label,
            absoluteValue: dataPointSum,
          });
        }
      });
      let completeSum = 0;
      dataPoints.forEach((dataPoint) => {
        completeSum += dataPoint.absoluteValue;
      });

      dataPoints.forEach((dataPoint) => {
        dataPoint.relativeValue =
          dataPoint.absoluteValue > 0 ? Math.round((dataPoint.absoluteValue / completeSum) * 10000) / 100 : 0;
      });
      doughnutChartStateSetter(dataPoints);
    } else {
      doughnutChartStateSetter([]);
    }
  }

  /**
   * Extracts the total energy loss from the telemetry response and sets the state signal.
   * The total energy loss is calculated for the given time window.
   * If the total energy loss is undefined, the state signal is not updated.
   * @param energyLossResponse - The telemetry response to extract the total energy loss from.
   * @param timeWindow - The time window for which the total energy loss is extracted.
   * @param stateSignalSetter - The setter function for the total energy loss state signal.
   */
  public extractAndSetTotalEnergyLoss(
    energyLossResponse: TelemetryResponse | null,
    timeWindow: string,
    stateSignalSetter: (value: number) => void
  ) {
    const energyLoss = energyLossResponse?.energy_loss;
    if (energyLoss) {
      const totalEnergyLoss = this.energyToolUtils.getTimeWindowEnergyLoss(energyLoss, timeWindow);
      if (totalEnergyLoss !== undefined) {
        stateSignalSetter(totalEnergyLoss);
      }
    }
  }

  /**
   * Extracts the expected energy loss per day from the given asset and sets it into the expectedEnergyLossPerDayStateSetter signal.
   * If the expected energy loss per day is undefined, the signal is not updated.
   * @param asset a Customer, Location, Building or GateSystem object
   * @param expectedEnergyLossPerDayStateSetter a setter function for the expected energy loss per day signal
   */
  public extractAndSetExpectedEnergyLossPerDay(
    asset: Customer | Location | Building | GateSystem,
    expectedEnergyLossPerDayStateSetter: (value: number) => void
  ) {
    if (asset.expectedEnergyLossPerDay !== undefined) {
      expectedEnergyLossPerDayStateSetter(asset.expectedEnergyLossPerDay);
    }
  }

  /**
   * Extracts the energy cost for a specified time window from the telemetry response
   * and sets the energy cost state. If the energy cost data is undefined, the state
   * is not updated.
   * @param energyLossResponse - The telemetry response containing energy cost data.
   * @param timeWindow - The time window for which the energy cost is extracted.
   * @param energyCostStateSetter - A function to set the extracted energy cost value.
   */

  public extractAndSetEnergyCost(
    energyLossResponse: TelemetryResponse | null,
    timeWindow: string,
    energyCostStateSetter: (value: number) => void
  ) {
    const energyCost = energyLossResponse?.energy_loss_costs;
    if (energyCost) {
      const totalEnergyCost = this.energyToolUtils.getTimeWindowEnergyLoss(energyCost, timeWindow);
      if (totalEnergyCost !== undefined) {
        energyCostStateSetter(totalEnergyCost);
      }
    }
  }

  /**
   * Extracts the cycle from the telemetry response and sets the cycle state.
   * If the time window is not "total", the number of cycles is calculated
   * from the given timeseries of cycles. Otherwise, the value of the most
   * recent cycle is taken.
   * @param telemetryResponse - The telemetry response to extract the cycle from.
   * @param timeWindow - The time window for which the cycle is extracted.
   * @param stateSignalSetter - The setter function for the cycle state.
   */
  public extractAndSetCycle(
    telemetryResponse: TelemetryResponse | null,
    timeWindow: string,
    stateSignalSetter: (value: { value: number; timestamp: string }) => void
  ) {
    if (telemetryResponse?.cycle) {
      const cycle = {
        value:
          timeWindow !== "total"
            ? this.energyToolUtils.getNumberOfCyclesFromCycleTimeseries(telemetryResponse.cycle)
            : telemetryResponse.cycle[0]?.value,
        timestamp: this.energyToolUtils.convertTimeStamp(telemetryResponse.cycle[0]?.timestamp),
      };
      stateSignalSetter(cycle);
    }
  }

  /**
   * Updates the temperature state with the most recent value from the telemetry response.
   * If the temperature is undefined or null, the state is set to undefined.
   * @param telemetryResponse - The telemetry response to extract the  temperature from.
   * @param tempKey - The key that determines if it is inside or outside temperature
   * @param outsideTempStateSetter - The setter function for the temperature state.
   */
  public extractAndSetTemp(
    telemetryResponse: TelemetryResponse | RealtimeTelemetryResponse | null,
    tempKey: "Display_Sensor_Temp" | "outside_temperature",
    tempStateSetter: (value: TelemetryEntry | undefined) => void
  ) {
    const tempObject = telemetryResponse?.[tempKey];
    if (tempObject) {
      let tempValue;
      let timestamp;
      if (Array.isArray(tempObject)) {
        tempValue = tempObject[0]?.value;
        timestamp = tempObject[0]?.timestamp;
      } else {
        tempValue = tempObject.value;
        timestamp = tempObject.timestamp;
      }
      const temp = tempValue !== undefined && tempValue !== null ? Math.round(tempValue) : undefined;
      if (isNotNullOrUndefined(temp)) {
        tempStateSetter({ value: temp, timestamp });
      } else {
        tempStateSetter(undefined);
      }
    } else {
      tempStateSetter(undefined);
    }
  }

  /**
   * Extracts the gate position duration, last open duration and longest open duration from the telemetry data
   * and sets the state of the corresponding signals.
   * @param telemetryResponse the telemetry data to extract the gate position duration, last open duration and longest open duration from
   * @param gatePositionDurationStateSetter a function that sets the state of the gate position duration signal
   * @param lastOpenDurationStateSetter a function that sets the state of the last open duration signal
   * @param longestOpenDurationStateSetter a function that sets the state of the longest open duration signal
   */
  public extractAndSetOpenDuration(
    telemetryResponse: TelemetryResponse | RealtimeTelemetryResponse | null,
    gatePositionDurationStateSetter?: (value: { value: number; timestamp: number }) => void,
    lastOpenDurationStateSetter?: (value: number) => void,
    longestOpenDurationStateSetter?: (value: number) => void
  ) {
    const gatePositionData = this.getPosition(telemetryResponse, false);

    if (gatePositionDurationStateSetter) {
      gatePositionDurationStateSetter(gatePositionData);
    }

    if (lastOpenDurationStateSetter && telemetryResponse?.last_open_duration) {
      let lastOpenDurationValue: number;
      if (Array.isArray(telemetryResponse.last_open_duration)) {
        lastOpenDurationValue = telemetryResponse.last_open_duration[0]?.value;
      } else {
        lastOpenDurationValue = telemetryResponse.last_open_duration.value;
      }

      lastOpenDurationStateSetter(lastOpenDurationValue);
    }
    if (longestOpenDurationStateSetter && telemetryResponse?.longest_open_duration) {
      let longestOpenDurationValue: number;
      if (Array.isArray(telemetryResponse.longest_open_duration)) {
        longestOpenDurationValue = telemetryResponse.longest_open_duration[0]?.value;
      } else {
        longestOpenDurationValue = telemetryResponse.longest_open_duration.value;
      }
      longestOpenDurationStateSetter(longestOpenDurationValue);
    }
  }

  /**
   * Extracts the gate movement position from the telemetry data and sets the
   * gate position state.
   * @param telemetryData the telemetry data to extract the gate movement position
   * from
   * @param gatePositionStateSetter a function that sets the gate position state
   */
  public extractAndSetGateMovementPos(
    telemetryData: TelemetryResponse | RealtimeTelemetryResponse | null,
    gatePositionStateSetter: (value: { value: number; timestamp: string }) => void
  ) {
    const gatePositionData = this.getPosition(telemetryData, true);
    gatePositionStateSetter(gatePositionData);
  }

  private getPosition(
    telemetryData: TelemetryResponse | RealtimeTelemetryResponse | null,
    convert: false
  ): {
    value: number;
    timestamp: number;
  };
  private getPosition(
    telemetryData: TelemetryResponse | RealtimeTelemetryResponse | null,
    convert: true
  ): {
    value: number;
    timestamp: string;
  };
  /**
   * Gets the gate position data from the telemetry data.
   * @param telemetryData the telemetry data to extract the gate position from
   * @param convert whether to convert the timestamp to a string
   * @returns an object with the value and timestamp of the gate position. If the telemetry data is null or undefined, the returned object will have undefined values.
   */
  private getPosition(
    telemetryData: TelemetryResponse | RealtimeTelemetryResponse | null,
    convert: boolean
  ): {
    value: number | undefined;
    timestamp: string | number | undefined;
  } {
    let value: number | undefined;
    let timestamp: string | number | undefined;
    if (telemetryData) {
      if (Array.isArray(telemetryData?.Gate_Movements_Pos)) {
        value = telemetryData?.Gate_Movements_Pos?.[0]?.value;
        timestamp = convert
          ? this.energyToolUtils.convertTimeStamp(telemetryData?.Gate_Movements_Pos?.[0]?.timestamp)
          : telemetryData?.Gate_Movements_Pos?.[0]?.timestamp;
      } else {
        value = telemetryData?.Gate_Movements_Pos?.value;
        timestamp = convert
          ? this.energyToolUtils.convertTimeStamp(telemetryData?.Gate_Movements_Pos?.timestamp)
          : telemetryData?.Gate_Movements_Pos?.timestamp;
      }
    }

    return {
      value,
      timestamp,
    };
  }
}
